import { useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/BlogPost.css";
import "../styles/MarathonChecklist.css";

// ── Checklist data ──────────────────────────────────────────────────────
// Each "leg" is one marker on the race-week timeline. Groups inside a leg
// are checkable; "note" and "example" groups render as callouts instead
// of checkboxes since they aren't tasks to complete.

const LEGS = [
  {
    marker: "-4",
    label: "4 Days Out",
    day: "Tuesday",
    groups: [
      {
        title: "Training",
        items: [
          "Workout — moderate effort for 40-60 minutes",
          "5 minutes breathing / visualizing",
          "Stretch and foam roll lightly",
        ],
      },
      {
        title: "Nutrition",
        items: [
          "Eat normal, balanced meals",
          "Focus on carbohydrates (rice, pasta, potatoes, oats, fruit)",
          "Stay hydrated throughout the day",
        ],
      },
      {
        title: "Logistics",
        items: [
          "Confirm travel plans w/ your support crew",
          "Review race course and start area",
          "Check race morning weather",
          "Put out race day clothes (socks, sunglasses, hat, sticky note reminders if needed)",
          "Lights out by 10:00pm",
        ],
      },
    ],
  },
  {
    marker: "-3",
    label: "3 Days Out",
    day: "Wednesday",
    groups: [
      {
        title: "Training",
        items: [
          "OFF DAY — stretch and do abs if you want, but no running or gym",
          "5 minutes breathing / visualizing",
          "Foot, PT, massage, or doctors appointment (if you think you need it)",
        ],
      },
      {
        title: "Gear check",
        items: [
          "Race shoes, already broken in — use inserts if you've trained with them",
          "Headphones, if allowed",
        ],
      },
      {
        title: "Fuel check",
        items: ["Race gels", "Salt tabs (if you use them)"],
        note: "Rule: nothing new on race day.",
      },
      {
        title: "Logistics",
        items: [
          "12:15pm pre-call — if there is a live-stream for the race, watch it",
          "Call your coach or a member of your support crew to talk through the plan",
          "Lights out around 9:45pm",
        ],
      },
    ],
  },
  {
    marker: "-2",
    label: "2 Days Out",
    day: "Thursday",
    groups: [
      {
        title: "Training",
        items: [
          "OFF DAY — no running, no gym",
          "5 minutes breathing / visualizing",
          "Stretch and roll lightly",
        ],
      },
      {
        title: "Hydration",
        items: [
          "Drink water consistently",
          "Add electrolytes — pack gels and electrolytes",
        ],
      },
      {
        title: "Carb loading begins",
        items: [
          "Increase carbs at meals",
          "Reduce greasy or heavy foods",
          "Avoid overeating",
        ],
      },
      {
        title: "Recovery",
        items: [
          "Drive to Omaha",
          "Good night's sleep — lights out around 9:30pm",
          "Stay off your feet when possible",
        ],
      },
    ],
  },
  {
    marker: "-1",
    label: "1 Day Out",
    day: "Friday",
    groups: [
      {
        title: "Shakeout run",
        items: ["15 minute easy jog", "5 minutes breathing / visualizing"],
      },
      {
        title: "Prepare",
        items: [
          "Prepare playlist — 4 hours long (throwbacks, I put the entire Hamilton soundtrack on mine)",
          "Take a nap today",
        ],
      },
      {
        title: "Lay out gear",
        items: [
          "Bib",
          "Safety pins",
          "Shoes",
          "Socks",
          "Tank",
          "Shorts",
          "Watch",
          "Gels",
          "Electrolytes",
        ],
      },
      {
        title: "Meals",
        items: [
          "High-carb dinner — aim for 5:00pm (eat your usual meal before a long run)",
          "Avoid spicy foods",
          "Avoid excess fiber",
        ],
      },
      {
        title: "Logistics",
        items: [
          "Visit where the race will start",
          "Plan how you'll get there and what time to wake up",
        ],
      },
      {
        title: "Sleep",
        items: ["Start reading in bed at 9:00pm — don't chase perfect sleep"],
        note: "The two nights before the race matter more than this one.",
      },
    ],
  },
  {
    marker: "0",
    label: "Race Day",
    day: "Saturday",
    groups: [
      {
        title: "2 hours before",
        items: ["Wake up", "Eat practiced breakfast", "Hydrate"],
        example: "Bagel · banana · PB&J · sports drink",
      },
      {
        title: "Before leaving",
        items: [
          "Bib attached",
          "Watch charged, GPS ready",
          "Gels packed",
          "Phone, ID, credit card, keys",
        ],
      },
      {
        title: "At the start line",
        items: [
          "Arrive early",
          "Use the restroom",
          "Light warmup",
          "Stay relaxed",
          "Do not start too fast",
        ],
      },
    ],
  },
];

const PACING = [
  { range: "Miles 1–13", note: "Hold back", pace: "8:30-8:45 /mi" },
  { range: "Miles 13–20", note: "Settle into your legs", pace: "8:45–9:00 /mi" },
  { range: "Mile 20+", note: "The race begins", pace: "Run by feel" },
];

const RACE_CUES = [
  "Run one mile per person — think of 26 different people and your memories of them",
  "Breathing: 7 in, 4 hold, 8 out",
  "Pump your arms",
  "Enjoy the views and the ride",
  "One foot in front of the other",
];

function ChecklistItem({ id, text, checked, onToggle }) {
  return (
    <li className={`mc-item${checked ? " is-checked" : ""}`}>
      <button
        type="button"
        className="mc-checkbox"
        role="checkbox"
        aria-checked={checked}
        aria-label={text}
        onClick={() => onToggle(id)}
      >
        <svg viewBox="0 0 16 16" className="mc-check-icon" aria-hidden="true">
          <path
            d="M3 8.5L6.3 12L13 4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <span className="mc-item-text" onClick={() => onToggle(id)}>
        {text}
      </span>
    </li>
  );
}

function MarathonChecklist() {
  const [checked, setChecked] = useState({});

  const toggle = (id) =>
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  const legStats = useMemo(() => {
    return LEGS.map((leg, legIdx) => {
      let total = 0;
      let done = 0;
      leg.groups.forEach((group, groupIdx) => {
        group.items.forEach((_, itemIdx) => {
          total += 1;
          const id = `${legIdx}-${groupIdx}-${itemIdx}`;
          if (checked[id]) done += 1;
        });
      });
      return { total, done };
    });
  }, [checked]);

  const overall = legStats.reduce(
    (acc, s) => ({ total: acc.total + s.total, done: acc.done + s.done }),
    { total: 0, done: 0 }
  );
  const overallPct = overall.total
    ? Math.round((overall.done / overall.total) * 100)
    : 0;

  return (
    <div className="marathon-checklist">
      <div className="mc-header">
        <div>
          <p className="mc-eyebrow">Race week, mile by mile</p>
          <h3 className="mc-title">The 4-Day-Out Checklist</h3>
        </div>
        <div className="mc-overall">
          <div className="mc-overall-ring" style={{ "--pct": `${overallPct}%` }}>
            <span>{overallPct}%</span>
          </div>
          <span className="mc-overall-label">race-ready</span>
        </div>
      </div>

      <div className="mc-track">
        {LEGS.map((leg, legIdx) => {
          const stats = legStats[legIdx];
          const pct = stats.total
            ? Math.round((stats.done / stats.total) * 100)
            : 0;
          return (
            <section className="mc-leg" key={leg.marker}>
              <div className="mc-leg-rail">
                <span className="mc-marker">{leg.marker}</span>
                <span className="mc-rail-line" aria-hidden="true" />
              </div>

              <div className="mc-leg-body">
                <div className="mc-leg-heading">
                  <div>
                    <h4>{leg.label}</h4>
                    <p className="mc-leg-day">{leg.day}</p>
                  </div>
                  <div className="mc-leg-progress">
                    <div className="mc-leg-progress-track">
                      <div
                        className="mc-leg-progress-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="mc-leg-progress-count">
                      {stats.done}/{stats.total}
                    </span>
                  </div>
                </div>

                <div className="mc-groups">
                  {leg.groups.map((group, groupIdx) => (
                    <div className="mc-group" key={group.title}>
                      <p className="mc-group-title">{group.title}</p>
                      <ul className="mc-item-list">
                        {group.items.map((text, itemIdx) => {
                          const id = `${legIdx}-${groupIdx}-${itemIdx}`;
                          return (
                            <ChecklistItem
                              key={id}
                              id={id}
                              text={text}
                              checked={!!checked[id]}
                              onToggle={toggle}
                            />
                          );
                        })}
                      </ul>
                      {group.example && (
                        <p className="mc-example">
                          <span>Example</span>
                          {group.example}
                        </p>
                      )}
                      {group.note && (
                        <p className="mc-note">{group.note}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );
        })}

        {/* ── Finish: during-the-marathon strategy, not a checklist ── */}
        <section className="mc-leg mc-leg--finish">
          <div className="mc-leg-rail">
            <span className="mc-marker mc-marker--finish">26.2</span>
          </div>
          <div className="mc-leg-body">
            <div className="mc-leg-heading">
              <div>
                <h4>During the Marathon</h4>
                <p className="mc-leg-day">Race strategy</p>
              </div>
            </div>

            <div className="mc-finish-grid">
              <div className="mc-finish-card">
                <p className="mc-group-title">Fuel &amp; hydration</p>
                <ul className="mc-plain-list">
                  <li>Gel every 30 minutes or so</li>
                  <li>Drink at every aid station</li>
                  <li>Don't wait until you're thirsty</li>
                </ul>
              </div>

              <div className="mc-finish-card">
                <p className="mc-group-title">Pacing</p>
                <table className="mc-pace-table">
                  <tbody>
                    {PACING.map((row) => (
                      <tr key={row.range}>
                        <td>{row.range}</td>
                        <td>{row.note}</td>
                        <td>{row.pace}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <blockquote className="mc-mantra">
              "The first 20 miles are a warm-up. The last 6.2 are the
              marathon."
            </blockquote>

            <div className="mc-finish-card">
              <p className="mc-group-title">Things to think about</p>
              <ul className="mc-plain-list">
                {RACE_CUES.map((cue) => (
                  <li key={cue}>{cue}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

// ── Blog post ───────────────────────────────────────────────────────────

function BlogPost13() {
  return (
    <>
      <Navbar />

      <article className="blog-post">
        {/* ── Breadcrumb ── */}
        <p className="blog-breadcrumb">
          <span>Life & Running</span>
          <span className="breadcrumb-divider">|</span>
          <span className="breadcrumb-section">
            Marathon | Training | Mindset
          </span>
        </p>

        {/* ── Headline & dek ── */}
        <h1 className="blog-headline">
          My First Marathon: The 4-Day Checklist That Got Me to the Start
          Line Ready
        </h1>

        <p className="blog-dek">
          26.2 miles is very physically demanding — you want to be ready.
          But it's not worth anxiously overthinking every little thing.
          Here's the checklist that got me there, and to the finish first.
        </p>

        {/* ── Share row ── */}
        <div className="blog-meta-row">
          <button
            className="blog-share-btn"
            onClick={() =>
              navigator.share?.({ title: document.title, url: window.location.href })
            }
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            Share
          </button>
        </div>

        {/* ── Date + read time ── */}
        <p className="blog-dateline">Race week &nbsp;|&nbsp; 4 min read</p>

        {/* ── Body copy ── */}
        <div className="blog-body">
          <p className="blog-drop-cap">
            I just ran my first marathon. 26.2 miles is very physically
            demanding, so you want to make sure you're ready. At the same
            time, it's not worth anxiously overthinking every little
            thing.
          </p>

          <p>
            Up until I decided to run the marathon, the most I had ever
            run consecutively was 13.1 miles — half of a marathon. When I
            committed to the race, I picked an 18-week training plan and
            got in 4–5 runs a week, using running as a way to stay fit and
            build the strength I'd need on race day. My longest training
            run was 19.34 miles. So 26.2 was always going to be a
            stretch. I don't regret how I trained for this first one though.
          </p>

          <p>
            I didn't go full marathon-junky mode until about 4 days out.
            To keep from overstressing, I put together a pre-race
            checklist. As long as I was crossing items off, I told myself
            I'd be ready. It worked out well: I finished in 87-degree
            heat, and I was the first woman across the line at the
            Nebraska Marathon.
          </p>

          <p>Here's the checklist, day by day.</p>
        </div>

        {/* ── Interactive checklist ── */}
        <MarathonChecklist />

        <div className="blog-body">
          <p className="blog-takeaway">
            <strong>Takeaway:</strong> Build your own ritual for race week.
            You don't need to overhaul your life 4 days out — you need a
            short list you trust, so your energy goes toward the run
            itself. Tap through the checklist above, save it, and make it
            yours.
          </p>
        </div>
      </article>

      <Footer />
    </>
  );
}

export default BlogPost13;